import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Anchor, BadgeCheck, Clock, Gift, Users, Waves, Fish, ShoppingBag, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { api } from "../lib/api";
import { toast } from "sonner";

const IMAGES = {
  hero: "/hero-boat.jpg",
  boat: "/boat-underway.jpg",
  sunset: "/boat-sunset.jpg",
  wreck: "/wreck-bass.jpg",
  harbour: "https://images.pexels.com/photos/18681171/pexels-photo-18681171.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  stag: "/stag-rut.jpg",
  mcnab: "/mcnab-stag.jpg",
  bream: "/bream-catch.jpg",
  mackerel: "/mackerel-catch.jpg",
  merch: {
    "heavyweight-hoodie": "/merch-hoodie-heavy.jpg",
    "lightweight-hoodie": "https://images.unsplash.com/photo-1564557287817-3785e38ec1f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxob29kaWUlMjBjbG90aGluZ3xlbnwwfHx8fDE3ODk5OTg3MDR8MA&ixlib=rb-4.1.0&q=85",
    "tshirt": "/merch-tshirt.jpg",
    "beanie": "/merch-beanie.jpg",
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.55, ease: "easeOut" } }),
};

async function buyNow(lookup_key, label) {
  try {
    const { data } = await api.post("/payments/checkout", {
      lookup_key, quantity: 1, origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(`Could not start checkout for ${label}.`);
  }
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="grain-overlay relative flex min-h-[88vh] items-end overflow-hidden" data-testid="hero">
      <img src={IMAGES.hero} alt="Shande III underway at dusk" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-slate-900/10" />
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-6">
        <motion.div initial="hidden" animate="show" variants={fadeUp}>
          <span className="inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-500/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-sky-100 backdrop-blur-sm" data-testid="hero-badge">
            <BadgeCheck className="h-4 w-4" /> First &amp; Only Licensed Bluefin Boat — Exmouth
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-6xl font-black uppercase leading-[0.9] tracking-tighter text-white sm:text-7xl lg:text-8xl" data-testid="hero-headline">
            Hunt Giant <span className="text-sky-400">Bluefin</span> Tuna
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-200 sm:text-lg" data-testid="hero-subcopy">
            Shande III is the first and only charter boat out of Exmouth licensed to fish for giant bluefin tuna
            on a catch-and-release licence. Ten hours offshore. Fish of a lifetime.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button data-testid="hero-book-button" onClick={() => navigate("/book")}
              className="btn-lift rounded-full bg-sky-500 px-7 py-6 text-base font-bold text-white hover:bg-sky-400">
              Book Your Trip <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button data-testid="hero-availability-button" variant="outline"
              onClick={() => document.getElementById("availability")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-lift rounded-full border-white/40 bg-white/10 px-7 py-6 text-base font-bold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white">
              Live Availability
            </Button>
          </div>
          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-200" data-testid="hero-stats">
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-sky-400" /> 10-hour offshore trips</span>
            <span className="flex items-center gap-2"><Users className="h-4 w-4 text-sky-400" /> 4–6 anglers per charter</span>
            <span className="flex items-center gap-2"><Anchor className="h-4 w-4 text-sky-400" /> Sailing from Exmouth Marina</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Trips({ trips }) {
  const navigate = useNavigate();
  const order = ["bluefin", "wreck", "bream", "mackerel", "stag-bluefin", "devon-mcnab"];
  const sorted = order.map((id) => trips.find((t) => t.id === id)).filter(Boolean);
  const imgFor = { bluefin: IMAGES.sunset, wreck: IMAGES.wreck, "stag-bluefin": IMAGES.stag, "devon-mcnab": IMAGES.mcnab, bream: IMAGES.bream, mackerel: IMAGES.mackerel };

  return (
    <section id="trips" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="trips-section">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
        <p className="section-eyebrow">Trips &amp; Prices</p>
        <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-slate-900 sm:text-5xl">
          Pick Your Day <span className="text-sky-500">On The Water</span>
        </h2>
      </motion.div>

      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {sorted.map((trip, i) => (
          <motion.article key={trip.id} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
            data-testid={`trip-card-${trip.id}`}
            className={`card-clean group flex flex-col overflow-hidden ${trip.id === "bluefin" ? "md:col-span-2 md:row-span-2" : ""}`}>
            {imgFor[trip.id] && (
              <div className={`relative overflow-hidden ${trip.id === "bluefin" ? "h-64 md:h-80" : "h-44"}`}>
                <img src={imgFor[trip.id]} alt={trip.name}
                  style={{ objectPosition: ["wreck", "bream", "mackerel"].includes(trip.id) ? "50% 20%" : "center" }}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <span className="absolute left-4 top-4 rounded-full bg-slate-900/70 px-3 py-1 text-xs font-bold uppercase tracking-wider text-sky-300 backdrop-blur-sm">
                  {trip.duration}
                </span>
              </div>
            )}
            <div className="flex flex-1 flex-col p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-sky-600">{trip.tagline}</p>
              <h3 className="mt-1 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-900">{trip.name}</h3>
              <p className="mt-2 text-sm text-slate-500">{trip.description}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {trip.species.map((s) => (
                  <span key={s} className="rounded-full bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">{s}</span>
                ))}
              </div>
              <div className="mt-5 space-y-2 border-t border-slate-100 pt-4">
                {trip.prices.map((p) => (
                  <div key={p.lookup_key} className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-slate-600">{p.label}</span>
                    <span className="font-display text-2xl font-extrabold text-slate-900">£{p.amount_gbp}</span>
                  </div>
                ))}
                {trip.prices.length > 0 && <p className="text-xs text-slate-400">{trip.prices[0].note}</p>}
              </div>
              {trip.coming_soon ? (
                <div data-testid={`trip-coming-soon-${trip.id}`}
                  className="mt-6 flex w-full items-center justify-center rounded-full bg-sky-100 py-2.5 font-bold uppercase tracking-wider text-sky-700">
                  Coming Soon
                </div>
              ) : (
                <Button data-testid={`trip-book-${trip.id}`} onClick={() => navigate(`/book?trip=${trip.id}`)}
                  className="btn-lift mt-6 w-full rounded-full bg-slate-900 font-bold text-white hover:bg-sky-600">
                  Book {trip.name}
                </Button>
              )}
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function Loyalty() {
  return (
    <section className="bg-sky-600" data-testid="loyalty-banner">
      <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-4 py-12 sm:px-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/15">
            <Fish className="h-6 w-6 text-white" />
          </span>
          <div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tight text-white">
              10 Trips. The 10th Is On Us.
            </h3>
            <p className="mt-1 max-w-xl text-sm text-sky-100">
              Book wreck or bream trips with us and every 10th trip is free. Our way of thanking the regulars
              who fish Shande III season after season.
            </p>
          </div>
        </div>
        <span className="rounded-full border border-sky-300/50 px-5 py-2 text-sm font-bold uppercase tracking-wider text-white">
          Wreck &amp; Bream Trips
        </span>
      </div>
    </section>
  );
}

function AvailabilitySection() {
  const navigate = useNavigate();
  return (
    <section id="availability" className="bg-slate-100 py-24" data-testid="availability-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
          <p className="section-eyebrow">Live Booking Calendar</p>
          <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-slate-900 sm:text-5xl">
            Real Dates. <span className="text-sky-500">Updated By Us.</span>
          </h2>
          <p className="mt-4 max-w-md text-slate-600">
            This calendar is live — we update it ourselves, so what you see is genuinely bookable.
            Pick an open day, pay securely online, and your space on Shande III is confirmed.
          </p>
          <Button data-testid="availability-book-button" onClick={() => navigate("/book")}
            className="btn-lift mt-8 rounded-full bg-sky-500 px-7 py-6 text-base font-bold text-white hover:bg-sky-600">
            Choose Your Date <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} variants={fadeUp} className="flex justify-center lg:justify-end">
          <AvailabilityCalendar mode="view" />
        </motion.div>
      </div>
    </section>
  );
}

function Vouchers({ voucher }) {
  if (!voucher) return null;
  return (
    <section id="vouchers" className="mx-auto max-w-7xl px-4 py-24 sm:px-6" data-testid="vouchers-section">
      <div className="card-clean grain-overlay relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900" />
        <img src={IMAGES.harbour} alt="Devon harbour at sunset" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative z-10 flex flex-col items-start gap-6 p-10 md:flex-row md:items-center md:justify-between md:p-14">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-sky-300">
              <Gift className="h-4 w-4" /> Gift Vouchers
            </p>
            <h2 className="mt-3 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl" data-testid="voucher-headline">
              £130 Voucher — Wrecking or Bream
            </h2>
            <p className="mt-3 max-w-lg text-sm text-slate-300">
              {voucher.description} The perfect present for the angler in your life — they pick the date, we provide the fish.
            </p>
          </div>
          <Button data-testid="voucher-buy-button" onClick={() => buyNow(voucher.lookup_key, voucher.name)}
            className="btn-lift shrink-0 rounded-full bg-sky-500 px-8 py-6 text-base font-bold text-white hover:bg-sky-400">
            Buy a Voucher — £130
          </Button>
        </div>
      </div>
    </section>
  );
}

function Merch({ items }) {
  return (
    <section id="merch" className="bg-white py-24" data-testid="merch-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="flex items-end justify-between">
          <div>
            <p className="section-eyebrow">Shande III Merchandise</p>
            <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-slate-900 sm:text-5xl">
              Wear The <span className="text-sky-500">Boat</span>
            </h2>
          </div>
          <ShoppingBag className="hidden h-10 w-10 text-sky-200 md:block" />
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <motion.article key={item.id} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fadeUp}
              data-testid={`merch-card-${item.id}`} className="card-clean group overflow-hidden border-slate-200 bg-slate-50">
              <div className="h-52 overflow-hidden">
                <img src={IMAGES.merch[item.id]} alt={item.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-5">
                <h3 className="font-display text-xl font-extrabold uppercase tracking-tight text-slate-900">{item.name}</h3>
                <p className="mt-1 text-xs text-slate-500">{item.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-2xl font-extrabold text-slate-900">£{item.amount_gbp}</span>
                  <Button data-testid={`merch-buy-${item.id}`} onClick={() => buyNow(item.lookup_key, item.name)} size="sm"
                    className="btn-lift rounded-full bg-sky-500 font-bold text-white hover:bg-sky-600">
                    Buy
                  </Button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2" data-testid="about-section">
      <div className="overflow-hidden rounded-2xl">
        <img src={IMAGES.boat} alt="Shande III cutting through blue water" className="h-full w-full object-cover" />
      </div>
      <div>
        <p className="section-eyebrow">The Boat</p>
        <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-slate-900 sm:text-5xl">
          An Up &amp; Coming Name Out Of <span className="text-sky-500">Exmouth</span>
        </h2>
        <p className="mt-4 text-slate-600">
          Shande III sails from Exmouth Marina on the Devon coast, gateway to Lyme Bay and the offshore wrecks.
          We specialise in wreck fishing and are well known for our Bass and Pollock — and we're the first and only
          boat here licensed to target giant bluefin tuna, all caught and released.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-4">
          {[
            { icon: Waves, label: "Lyme Bay & offshore wrecks" },
            { icon: BadgeCheck, label: "Catch & release bluefin licence" },
            { icon: Users, label: "Min 4, max 6 anglers on bluefin trips" },
            { icon: Fish, label: "Bass, Pollock, John Dory, Cod & more" },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-sky-500" />
              <span className="text-sm font-medium text-slate-700">{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [trips, setTrips] = useState([]);
  const [merch, setMerch] = useState({ items: [], voucher: null });

  useEffect(() => {
    api.get("/trips").then((res) => setTrips(res.data)).catch(() => {});
    api.get("/merch").then((res) => setMerch(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <Navbar />
      <Hero />
      <Trips trips={trips} />
      <Loyalty />
      <AvailabilitySection />
      <Vouchers voucher={merch.voucher} />
      <Merch items={merch.items} />
      <About />
      <Footer />
    </div>
  );
}
